import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AppUser } from '@ats/cm/core';
import { CmUIService } from '@ats/cm/ui';
import { IpClientService, IpUserService } from '@ats/ip/core';
import { AtsNotificationService } from '@ats/notifications';
import { environment } from '@ip-env/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, map, startWith, switchMap, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
    selector: 'ip-view',
    templateUrl: './view.component.html',
    styleUrls: ['./view.component.scss'],
    standalone: false
})
export class IpViewComponent implements OnInit {
	hideUI$: Observable<boolean>;
	skipClientSelection$: Observable<boolean>;
	clientSelectList$: Observable<string[] | null>;
	user$: Observable<AppUser>;
	footerDateCurrentYear = new Date().getFullYear();
	readonly environment: 'local' | 'beta' | 'prod';

	constructor(
		private ui: CmUIService,
		private clientService: IpClientService,
		private router: Router,
		private route: ActivatedRoute,
		private user: IpUserService,
		private readonly notifications: AtsNotificationService,
		private readonly snackbar: MatSnackBar
	) {
		this.environment = this.getEnvironment();
		this.notificationWatch();
	}

	ngOnInit() {
		this.hideUI$ = this.ui.hideUI$;
		this.user$ = this.user.user$;

		const routeRequiresClient$ = this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			map(() => this.checkIfClientRequired()),
			startWith(this.checkIfClientRequired())
		);

		const clientSelectorDisabledByRoute$ = this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			map(() => this.checkIfClientSelectorDisabled()),
			startWith(this.checkIfClientSelectorDisabled())
		);

		// Route requires a client to be selected (for skip/require client selection UI)
		const clientIsRequired$ = combineLatest([
			this.user.authenticated$,
			routeRequiresClient$,
		]).pipe(
			map(([authenticated, routeRequiresClient]) => authenticated && routeRequiresClient)
		);

		// Client selector dropdown enabled (false = disabled). Disabled when route has clientSelectorDisabled.
		combineLatest([
			this.user.authenticated$,
			routeRequiresClient$,
			clientSelectorDisabledByRoute$,
		])
			.pipe(
				map(
					([authenticated, routeRequiresClient, clientSelectorDisabled]) =>
						authenticated && routeRequiresClient && !clientSelectorDisabled
				),
				tap(enableSelector => this.clientService.setClientListDisabledStatus(!enableSelector)),
				untilDestroyed(this)
			)
			.subscribe();

		this.skipClientSelection$ = clientIsRequired$.pipe(
			switchMap(clientIsRequired => (clientIsRequired ? this.clientService.active$ : of(true))),
			map(hasActiveClient => !!hasActiveClient)
		);

		this.clientSelectList$ = this.clientService.clients$;
	}

	logout(): void {
		this.user.clearUser();
	}

	private notificationWatch(): Subscription {
		return this.notifications.notice$.pipe(untilDestroyed(this)).subscribe(notice => {
			this.snackbar.open(notice.message, notice.action, {
				panelClass: notice.type.toString().toLocaleLowerCase().split(' ').join('-'),
				horizontalPosition: 'end',
				verticalPosition: 'top',
			});
		});
	}

	private checkIfClientRequired(): boolean {
		const leaf = this.getLeafRoute(this.route);
		return leaf ? ((leaf.snapshot || {}).data || {}).clientRequired || false : false;
	}

	private checkIfClientSelectorDisabled(): boolean {
		const leaf = this.getLeafRoute(this.route);
		return leaf ? ((leaf.snapshot || {}).data || {}).clientSelectorDisabled === true : false;
	}

	private getLeafRoute(route: ActivatedRoute): ActivatedRoute | null {
		let child = route.firstChild;
		while (child) {
			if (child.firstChild) {
				child = child.firstChild;
			} else {
				return child;
			}
		}
		return null;
	}

	private getEnvironment(): 'local' | 'beta' | 'prod' {
		if (environment.local) {
			return 'local';
		}
		if (environment.beta) {
			return 'beta';
		}
		return 'prod';
	}
}
